// Create New Order — client-side seed construction (design doc §5A/§5B).
//
// These are pure, display-only transforms: `getOrderDetails('T###')` (template
// path) or an empty shell (blank path) shaped into an `OrderDetails` the drawer
// can render and edit like any other pro. The SERVER owns the authoritative
// template-header copy at create time (reusing `translate_to_overrides`) — this
// seed only needs to be a superset the user can see/edit; it is never sent to
// `POST /orders` verbatim (useOrderEditing's save() diffs `editedOrderFields`
// against this seed via `buildOrderDataRows`).
import type { InvoiceMain, OrderDetails, OrderLeg } from '../../../services/orders.service';
import { isTemplatePronumber } from '../templateFieldPolicy';

/** Modal field snapshot (CreateOrderModal) that seeds both create paths. */
export interface CreateOrderModalFields {
  movetype: string;
  bol: string;
  container: string;
  billTo: { recno: number; name: string };
  customer: { recno: number; name: string };
  useTemplate: 'Y' | 'N';
  delivertofilter: string;
}

// ---------------------------------------------------------------------------
// Template path — buildCreateOrderSeed
// ---------------------------------------------------------------------------

/**
 * Leg fields cleared for a create-mode seed: driver/dispatch assignment and
 * every appointment/arrival/departure/geo datetime — none of that can exist
 * on an order that hasn't been created yet. Routing fields (legnum/legtype/
 * legAction2/legDrop/origin+dest codes+names/miles) ride through unchanged —
 * V1's form carried them from the template (v1-report §4.4a).
 *
 * EXCEPTION — the four appt TIME columns (`origApptTime1`/`2`,
 * `destApptTime1`/`2`) ride through with the routing fields. An appointment
 * WINDOW is blueprint data: the customer's dock is open 08:00-16:00 whatever
 * day the order lands. Only the DATE beside it is unknowable in advance, so
 * `origApptDate`/`destApptDate` stay cleared — the new order carries a window
 * with no day and the scheduler sets the day.
 *
 * This is V1 parity, not a new idea: V1's CREATEPRO branch (funcs.pl
 * getLegData) selects the template's leg row wholesale and clears only
 * `recno_all`, so all four times rode into the new order's form; `@dupelegs`
 * (lib_editrecord.pl) carries the same four on an order dupe. The origin side
 * matters even though it isn't directly editable on a template (State 1): a
 * leg's origin window IS the previous leg's destination window — the same
 * physical stop — and useOrderEditing's cascade mirrors it there on every dest
 * edit. Measured on prod: 1,836 of the 1,863 template legs carrying an
 * origappttime1 hold exactly the previous leg's destappttime1, and only ONE
 * leg-1 origin window exists table-wide. Dropping the origin side would hand
 * the new order a leg-1 delivery window and a blank leg-2 pickup window for
 * the very same dock.
 *
 * `legrecno` is
 * intentionally left as-is here; `useOrderEditing`'s reset effect is what
 * replaces it with a negative temp id when it seeds `pendingNewLegs` (design
 * doc §4.1) — doing it here would just get overwritten.
 */
function clearLegForCreate(leg: OrderLeg): OrderLeg {
  return {
    ...leg,
    driver: '',
    dispatchid: 0,
    dspdriverdatetime: '',
    origApptDate: '',
    // origApptTime1/origApptTime2 deliberately NOT cleared — see the note above.
    origApptNumber: '',
    origArrivalDateTime: null,
    origDepartDateTime: null,
    destApptDate: '',
    // destApptTime1/destApptTime2 likewise NOT cleared.
    destApptNumber: '',
    destArrivalDateTime: null,
    destDepartDateTime: null,
    origgeoarrivaldatetime: '',
    origgeodepartdatetime: '',
    destgeoarrivaldatetime: '',
    destgeodepartdatetime: '',
    origapptrequesteddatetime: '',
    destapptrequesteddatetime: '',
    acceptdatetime: '',
    donotpayleg: '',
    driverPayRate: null,
    driverPayFuelType: '',
    driverPayStatus: '',
    driverPayDate: '',
    driverPayApprovedBy: '',
    driverPayApprovedDatetime: '',
    driverPayRecno: 0,
    driverPayDriver: '',
    driverPayAllowEdit: false,
    dispatchStatus: undefined,
    errorReason: undefined,
    sendToGwiMove: false,
    isPaid: false,
    missingApptDate: false,
    plannedWithSmartPreplan: false,
  };
}

/**
 * Template path: build the create-mode seed from a fetched template's
 * `OrderDetails` (`getOrderDetails('T###')`, already template-aware) plus the
 * CreateOrderModal's fields. Everything NOT listed below rides through via
 * the spread — that's V1's implicit same-named-column template copy
 * (size, equipType, chassis*, commodity, templateType, isDrop, otr, scale,
 * hazmat, dispatcher, csr, whseCsrUsername, cstDispatcherUsername, scac,
 * mode/rail, consignee/broker/billToContact fields, gwiMove, revenueRegion,
 * marginLimitLocal, invoiceApprovedBy/Datetime, rateComments, etc. — design
 * doc section 5A "kept implicitly").
 */
export function buildCreateOrderSeed(
  templateDetails: OrderDetails,
  modal: CreateOrderModalFields,
): OrderDetails {
  // Template reads return the template's own "T####" id in the `pronumber`
  // field (cms.template.pronumber is varchar(15), unlike ordersall's numeric
  // column) — the FE type says `number` but the runtime value for a template
  // read is the string, same looseness ChangeTemplateModal already relies on
  // (`String(pronumber)`, ChangeTemplateModal.tsx:239/330).
  const templateId = String(templateDetails.pronumber);
  const movetypeOverride = modal.movetype.trim() !== '' ? modal.movetype : templateDetails.moveType;

  return {
    ...templateDetails,

    // Identity
    pronumber: 0,

    // Modal overrides (V1 lib_editrecord.pl:372-399) — bol/container/movetype/
    // billto/customercode win over the template. moveType is a DELIBERATE
    // deviation from V1 (which blindly overwrites with a blank modal movetype):
    // we keep the template's movetype when the modal left it blank.
    bookingNumber: modal.bol,
    container: modal.container,
    moveType: movetypeOverride,
    billTo: modal.billTo.recno,
    billToName: modal.billTo.name,
    customer: modal.customer.recno,
    customerName: modal.customer.name,

    // Blanked audit fields (V1 funcs.pl:539-547) — this is a new order, it has
    // no creation/edit history yet.
    createdBy: '',
    createdByName: '',
    createdDatetime: '',
    lastChangedBy: '',
    lastChangedByName: '',
    lastChangedDatetime: '',

    // Set
    createdFrom: 'template',
    parentId: templateId,
    template: templateId,
    masterPro: 'NEW MASTER', // sentinel; server resolves to the real pronumber at create
    cod: 'N',
    isExam: 'N',
    addlInfoDeleted: 'N',
    noteCount: 0, // notes are NOT copied from the template (V1 parity)
    warnings: [],

    // Legs: routing kept, driver/dispatch/appointment state cleared.
    legs: templateDetails.legs.map(clearLegForCreate),

    // Line items: pass through, but recno can't ride as the template's real
    // recno — none of these rows exist yet. `InvoiceLineItem.recno` is a
    // non-nullable `number` (unlike the save-payload's `invliData` rows, which
    // use `recno: null` — that shape lives in useOrderEditing's save branch,
    // not here), so we use the repo's existing "not a real row yet" sentinel
    // instead of `null` (see BillToLookupField's `{recno: 0, name: ''}`,
    // AdditionalChargesRatesDrawer.tsx:74 `r.recno > 0 ? r.recno : 0`).
    invoiceLineItems: (templateDetails.invoiceLineItems ?? []).map((li) => ({
      ...li,
      recno: 0,
    })),

    // Invoice header + hazmat lines pass through as-is (spread already carries
    // them). The template's invoice bill-to concept has no FE-visible field —
    // `InvoiceMain` has no `billTo` column (see report) — so there is nothing
    // to override here; the order-level `billTo` above is what the backend
    // reads when it re-derives the invoice row at create.
  };
}

// ---------------------------------------------------------------------------
// Blank path — buildBlankOrderSeed
// ---------------------------------------------------------------------------

const TWO_LEG_MOVETYPES = new Set(['IMPORT', 'EXPORT']);
const TERMINATE_LEG_TWO_MOVETYPES = new Set(['IMPORT', 'EXPORT', 'INBOUND', 'OUTBOUND']);

function blankLeg(legnum: number, legtype: string, legAction2: string): OrderLeg {
  return {
    legrecno: 0,
    legnum,
    legtype,
    origcoordinates: '',
    origcode: 0,
    origApptDate: '',
    origName: '',
    origCityState: '',
    legAction2,
    destcoordinates: '',
    destcode: 0,
    destApptDate: '',
    destName: '',
    destCityState: '',
    legDrop: 'N',
    driver: '',
    loadedLeg: 0,
  };
}

/**
 * No-template leg skeleton (templates.py:new_template_legs/terminateleg_for
 * pattern). IMPORT/EXPORT get a PICKUP→DELIVERY leg followed by a
 * DELIVERY→TERMINATE leg; every other movetype — INCLUDING a blank one — gets
 * a single PICKUP→DELIVERY leg. That blank-movetype case is a DELIBERATE
 * deviation from V1, which seeded zero legs for a blank movetype (design doc
 * §5B / plan "Deviate (deliberate)"); a legless order can't satisfy the
 * "≥1 PICKUP + ≥1 DELIVERY leg" create-save rule, so we always seed one.
 */
function buildBlankLegSkeleton(movetype: string): OrderLeg[] {
  const mt = movetype.trim().toUpperCase();
  if (TWO_LEG_MOVETYPES.has(mt)) {
    return [blankLeg(1, 'PICKUP', 'DELIVERY'), blankLeg(2, 'DELIVERY', 'TERMINATE')];
  }
  return [blankLeg(1, 'PICKUP', 'DELIVERY')];
}

/**
 * terminateleg pointer for the no-template path. IMPORT/EXPORT/INBOUND/
 * OUTBOUND get 2 (matching `templates.py:_TERMINATE_LEG_MOVETYPES`) even
 * though INBOUND/OUTBOUND only get ONE leg from `buildBlankLegSkeleton` above
 * — a dangling pointer that doesn't resolve to a real leg is harmless because
 * `buildLegSavePayload` recomputes the pointers from the actual legs at save
 * time (design doc §5B quirk note). Every other movetype (incl. blank) gets 0.
 */
function terminateLegFor(movetype: string): number {
  return TERMINATE_LEG_TWO_MOVETYPES.has(movetype.trim().toUpperCase()) ? 2 : 0;
}

function blankInvoiceMain(): InvoiceMain {
  return {
    recno: 0,
    invoiceDate: '',
    masExportDate: '',
    // V1's form default, same reasoning as the Y/N flag block below: Bill From is
    // a select over ('CMS','DO NOT BILL') with no blank option, so a V1 create POST
    // always carried 'CMS'. The server stamps it either way (the invoice skeleton
    // seed + create_defaults.INVOICE_CREATE_FORM_DEFAULTS); seeding it here keeps
    // the drawer showing what the save will actually persist.
    billFromSystem: 'CMS',
    doNotBillReason: '',
    creditAmount: 0,
    creditReason: '',
    invoiceTotal: 0,
    invoiceComments: '',
    quoteNumber: 0,
    revisionNumber: 0,
    v2QuoteNumber: 0,
    v2RevisionNumber: 0,
    v2Lane: '',
    requireEdi: 'N',
    requireMrpl: 'N',
    requireInInterchange: 'N',
    requirePod: 'N',
    requireBol: 'N',
    requireOutInterchange: 'N',
    requireChassisInEir: 'N',
    requireChassisOutEir: 'N',
    requireRevisedDo: 'N',
    pendingApproval: 'N',
    estInvoiceEmail: '',
    billToRefNo: '',
    // A create-mode order has no invoice yet, so it has no invoice number.
    invoiceNumber: '',
  };
}

export interface BuildBlankOrderSeedOptions {
  /**
   * What the seeded record calls itself (#33471). Load-bearing, not cosmetic:
   * OrderFormView, AddlInfoView, AddlCostView and TracingView all decide
   * `isTemplate` from `details.pronumber` — not from their pronumber prop — so a
   * template seed that reports 0 makes every one of them render the ORDER branch.
   * That is what left the whole LC field set open on a template create, and the
   * order-lc-fields permission components visible on a record that is not an order.
   *
   * Pass the template create sentinel ('TNEW') for a template. Defaults to 0,
   * which is what an order seed has always carried.
   */
  pronumber?: number | string;
}

/**
 * No-template path: an empty `OrderDetails` shell (V1 funcs.pl:268-312,
 * 1262-1287) plus the modal's fields and a movetype-driven leg skeleton.
 */
export function buildBlankOrderSeed(
  modal: CreateOrderModalFields,
  opts: BuildBlankOrderSeedOptions = {},
): OrderDetails {
  const movetype = modal.movetype ?? '';

  return {
    // Required OrderDetails fields — every string '', every Y/N flag 'N'.
    // The cast matches reality rather than adding a new lie: OrderDetails types
    // `pronumber` as number, but a real template's details carry a "T…" STRING —
    // which is exactly why isTemplatePronumber accepts `string | number` and why
    // the four views above can work at all. Widening the field properly is a
    // separate job (it breaks ~15 order-only call sites in dupe/reuse/haz).
    pronumber: (opts.pronumber ?? 0) as OrderDetails['pronumber'],
    moveType: movetype,
    billToName: modal.billTo.name,
    billTo: modal.billTo.recno,
    customerName: modal.customer.name,
    keyAccount: '',
    dispatcherDisplayName: '',
    dispatcherRegion: '',
    hot: 'N',
    isDaycab: 'N',
    hazmat: 'N',
    isTank: 'N',
    otr: 'N',
    overweightPermitRequired: 'N',
    oversizePermitRequired: 'N',
    railbilled: 'N',
    // #33560 — the rest of V1's `select`/@yesno_data flag block (defs.pl:2367).
    // These are optional on OrderDetails, so the seed left them undefined and the
    // drawer rendered a BLANK dropdown for a column that is only ever Y or N. The
    // server now stamps 'N' on the create either way (create_defaults'
    // ORDER_CREATE_FORM_DEFAULTS), so leaving them out here just meant the drawer
    // disagreed with what the save would persist.
    scale: 'N',
    swing: 'N',
    // Street Turn Review is the `mule` column (buildOrderDataRows' mapping).
    streetTurnReview: 'N',
    otherHolds: 'N',
    inbondTenderRcvd: 'N',
    // triaxle is on the ORDER layout only — V1 never wrote it on a template
    // create, and the server's order-only set matches that, so a template seed
    // must leave it undefined rather than show an 'N' that will not be saved.
    ...(isTemplatePronumber(opts.pronumber ?? 0) ? {} : { triaxle: 'N' }),
    container: modal.container,
    size: '',
    equipType: '',
    isGwiEquip: 'N',
    flexBooking: 'N',
    chassisId: '',
    chassisType: '',
    chassisProvider: '',
    scac: '',
    lastFreeDay: '',
    storageClearedDate: '',
    custTargetDate: '',
    targetDeliveryDate: '',
    targetOnOrBy: '',
    perDiem: '',
    firstRcv: '',
    cutoffDatetime: '',
    dropReleaseDatetime: '',
    dropReleaseComment: '',
    bobtailApproved: 'N',

    // Modal-derived
    customer: modal.customer.recno,
    bookingNumber: modal.bol,

    // V1 funcs.pl new-order defaults
    // LC (csr) starts BLANK and the user picks one — requested 2026-10-16. It used
    // to seed the person opening the modal, which quietly made whoever clicked
    // "create" the coordinator of record on every order and template. Both save
    // paths already refuse a blank LC (useOrderEditing's required-field chain for an
    // order, its isTemplate branch for a template, and order_create_service's 422),
    // so nothing can be created without one — it just has to be chosen now.
    //
    // The from-template path is unaffected and deliberately so: `buildCreateOrderSeed`
    // spreads the template's own details, so an order created from a template inherits
    // the TEMPLATE's LC. (An inactive one is blanked server-side by
    // order_create_service's `csr_inactive`, which is also where the old client-side
    // blanking helper's job now lives entirely.)
    csr: '',
    sendEmailNotifications: 'Y',
    masterPro: 'NEW MASTER',
    createdFrom: 'user',
    cod: 'N',
    isExam: 'N',
    addlInfoDeleted: 'N',
    noteCount: 0,
    warnings: [],

    // Leg skeleton — templates.py:new_template_legs/terminateleg_for pattern.
    originateLeg: 1,
    deliveryLeg: 1,
    terminateLeg: terminateLegFor(movetype),
    legs: buildBlankLegSkeleton(movetype),

    invoiceMain: blankInvoiceMain(),
    invoiceLineItems: [],
  };
}
